import { useMemo, useState } from "react";
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  History,
  Search,
} from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { APP_VERSION, APP_VERSION_DATE, APP_VERSION_NAME } from "@/version";
import changelogSource from "../../../../../../CHANGELOG.md?raw";
import {
  parseChangelog,
  type ChangeType,
  type ChangelogVersion,
} from "../parseChangelog";

const FILTER_LABELS: { key: ChangeType | "All"; label: string }[] = [
  { key: "All", label: "All" },
  { key: "Added", label: "Added" },
  { key: "Changed", label: "Changed" },
  { key: "Fixed", label: "Fixed" },
  { key: "Removed", label: "Removed" },
  { key: "Security", label: "Security" },
];

const TYPE_STYLES: Record<ChangeType, { dot: string; pill: string }> = {
  Added: {
    dot: "bg-emerald-500",
    pill: "bg-emerald-100 text-emerald-700",
  },
  Changed: {
    dot: "bg-sky-500",
    pill: "bg-sky-100 text-sky-700",
  },
  Fixed: {
    dot: "bg-amber-500",
    pill: "bg-amber-100 text-amber-700",
  },
  Removed: {
    dot: "bg-slate-500",
    pill: "bg-slate-200 text-slate-700",
  },
  Security: {
    dot: "bg-rose-500",
    pill: "bg-rose-100 text-rose-700",
  },
  Deprecated: {
    dot: "bg-orange-500",
    pill: "bg-orange-100 text-orange-700",
  },
};

const CARD_BORDER = "hsl(210,30%,85%)";

const GITHUB_REPO_URL = "https://github.com/PhazeX-AI/phazex-hub";

function commitUrl(hash: string): string {
  return `${GITHUB_REPO_URL}/commit/${hash}`;
}

function renderEntryText(text: string) {
  const parts = text.split(/(`[^`]+`)/g);
  return parts.map((part, i) => {
    if (part.startsWith("`") && part.endsWith("`")) {
      return (
        <code
          key={i}
          className="rounded bg-[hsl(210,30%,94%)] px-1 py-[1px] text-[12px] font-mono text-[hsl(215,16%,30%)]"
        >
          {part.slice(1, -1)}
        </code>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

export default function ChangelogPage() {
  const versions = useMemo<ChangelogVersion[]>(
    () => parseChangelog(changelogSource),
    [],
  );

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<ChangeType | "All">("All");
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    versions.forEach((v, i) => {
      initial[v.version] = i === 0;
    });
    return initial;
  });

  const normalizedSearch = search.trim().toLowerCase();

  const filtered = useMemo(() => {
    return versions.map((v) => {
      const sections = v.sections
        .filter((s) => filter === "All" || s.type === filter)
        .map((s) => ({
          ...s,
          entries: normalizedSearch
            ? s.entries.filter((e) =>
                e.text.toLowerCase().includes(normalizedSearch),
              )
            : s.entries,
        }))
        .filter((s) => s.entries.length > 0);

      const matched = sections.reduce((sum, s) => sum + s.entries.length, 0);
      return { ...v, sections, totalEntries: v.totalEntries, matched };
    });
  }, [versions, filter, normalizedSearch]);

  const isSearchingOrFiltering = filter !== "All" || normalizedSearch.length > 0;
  const visibleVersions = isSearchingOrFiltering
    ? filtered.filter((v) => v.matched > 0)
    : filtered;

  const totalMatched = visibleVersions.reduce((sum, v) => sum + v.matched, 0);

  function toggleVersion(version: string) {
    setExpanded((prev) => ({ ...prev, [version]: !prev[version] }));
  }

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Administration" },
        { label: "Changelog" },
      ]}
    >
      <div className="w-full space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="flex items-center gap-3">
            <History
              className="h-[18px] w-[18px] text-foreground"
              strokeWidth={1.5}
            />
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">
                Changelog
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Current release: v{APP_VERSION}
                {APP_VERSION_NAME ? ` — ${APP_VERSION_NAME}` : ""} ·{" "}
                {APP_VERSION_DATE}
              </p>
            </div>
          </div>
        </div>

        <div
          className="rounded-xl border bg-card p-4"
          style={{ borderColor: CARD_BORDER }}
        >
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
            <div className="relative flex-1">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                strokeWidth={1.5}
              />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search features, fixes, improvements…"
                className="h-9 w-full rounded-md border border-input bg-background pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
              />
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              {FILTER_LABELS.map((opt) => {
                const active = filter === opt.key;
                const isType = opt.key !== "All";
                const accent =
                  isType ? TYPE_STYLES[opt.key as ChangeType].dot : "bg-primary";
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setFilter(opt.key)}
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
                      active
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border text-muted-foreground hover:bg-[hsl(210,50%,96%)] hover:text-foreground",
                    )}
                  >
                    <span
                      className={cn("h-1.5 w-1.5 rounded-full", accent)}
                    />
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
          {isSearchingOrFiltering && (
            <div className="mt-3 text-xs text-muted-foreground">
              {totalMatched === 0
                ? "No entries match the current filters."
                : `Showing ${totalMatched} ${totalMatched === 1 ? "entry" : "entries"} across ${visibleVersions.length} ${visibleVersions.length === 1 ? "release" : "releases"}.`}
            </div>
          )}
        </div>

        <div className="space-y-3">
          {visibleVersions.length === 0 ? (
            <div
              className="rounded-xl border bg-card py-12 text-center text-sm text-muted-foreground"
              style={{ borderColor: CARD_BORDER }}
            >
              No changelog entries to show.
            </div>
          ) : (
            visibleVersions.map((v) => {
              const open = expanded[v.version] ?? false;
              const isCurrent = v.version === APP_VERSION;
              const hasBreaking = v.sections.some((s) =>
                s.entries.some((e) => e.breaking),
              );
              return (
                <div
                  key={v.version}
                  className="overflow-hidden rounded-xl border bg-card"
                  style={{ borderColor: CARD_BORDER }}
                >
                  <button
                    type="button"
                    onClick={() => toggleVersion(v.version)}
                    className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-[hsl(210,50%,97%)]"
                  >
                    {open ? (
                      <ChevronDown
                        className="h-4 w-4 shrink-0 text-muted-foreground"
                        strokeWidth={1.5}
                      />
                    ) : (
                      <ChevronRight
                        className="h-4 w-4 shrink-0 text-muted-foreground"
                        strokeWidth={1.5}
                      />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-base font-semibold text-foreground">
                          Version {v.version}
                        </span>
                        {isCurrent && (
                          <Badge variant="default" className="text-[10px]">
                            Current
                          </Badge>
                        )}
                        {hasBreaking && (
                          <Badge
                            variant="destructive"
                            className="inline-flex items-center gap-1 text-[10px]"
                          >
                            <AlertTriangle
                              className="h-3 w-3"
                              strokeWidth={1.5}
                            />
                            Breaking
                          </Badge>
                        )}
                      </div>
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        {v.date || v.rawDate}
                        {isSearchingOrFiltering
                          ? ` · ${v.matched} matching ${v.matched === 1 ? "entry" : "entries"}`
                          : v.totalEntries > 0
                            ? ` · ${v.totalEntries} ${v.totalEntries === 1 ? "entry" : "entries"}`
                            : ""}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-1">
                      {v.sections.map((s) => (
                        <span
                          key={s.type}
                          className={cn(
                            "rounded-full px-2 py-0.5 text-[10px] font-medium",
                            TYPE_STYLES[s.type].pill,
                          )}
                        >
                          {s.type} {s.entries.length}
                        </span>
                      ))}
                    </div>
                  </button>

                  {open && (
                    <div
                      className="space-y-4 border-t px-4 py-4"
                      style={{ borderColor: CARD_BORDER }}
                    >
                      {v.sections.length === 0 ? (
                        <div className="text-sm text-muted-foreground">
                          No entries.
                        </div>
                      ) : (
                        v.sections.map((s) => (
                          <div key={s.type}>
                            <div className="mb-2 flex items-center gap-2">
                              <span
                                className={cn(
                                  "h-2 w-2 rounded-full",
                                  TYPE_STYLES[s.type].dot,
                                )}
                              />
                              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                {s.type}
                              </span>
                              <span className="text-[11px] text-muted-foreground">
                                ({s.entries.length})
                              </span>
                            </div>
                            <ul className="space-y-1.5 pl-4">
                              {s.entries.map((e, idx) => (
                                <li
                                  key={idx}
                                  className="text-sm leading-relaxed text-foreground"
                                >
                                  <span className="inline-flex flex-wrap items-baseline gap-1.5">
                                    <span
                                      className={cn(
                                        "inline-flex shrink-0 -translate-y-px items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium",
                                        TYPE_STYLES[s.type].pill,
                                      )}
                                    >
                                      {s.type}
                                    </span>
                                    {e.breaking && (
                                      <span className="inline-flex shrink-0 -translate-y-px items-center gap-1 rounded-full bg-rose-100 px-1.5 py-0.5 text-[10px] font-medium text-rose-700">
                                        <AlertTriangle
                                          className="h-2.5 w-2.5"
                                          strokeWidth={1.5}
                                        />
                                        Breaking
                                      </span>
                                    )}
                                    <span>{renderEntryText(e.text)}</span>
                                    {e.commits.map((c) => (
                                      <a
                                        key={c}
                                        href={commitUrl(c)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-0.5 rounded bg-[hsl(210,30%,94%)] px-1.5 py-0.5 text-[10px] font-mono text-[hsl(215,16%,40%)] transition-colors hover:bg-[hsl(210,30%,90%)] hover:text-foreground"
                                      >
                                        {c.slice(0, 7)}
                                        <ExternalLink
                                          className="h-2.5 w-2.5"
                                          strokeWidth={1.5}
                                        />
                                      </a>
                                    ))}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </AppLayout>
  );
}
